import {Expressions, Component} from '@packages/systems/wfdl';
import {
  getElementBindingContextNode,
  type InternalBindingContextGraphNode,
} from '../BindingContextGraph';
import {analyzeSourceElement} from './analyzers';
import {
  getGenericError,
  parseErrorMessageToErrorCode,
  type ErrorCode,
} from './errors';
import {getValidationErrorMessage} from './validators';

import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import type {DynamicContext} from './types';
import {GatewayRuntimeEnvironment} from '../../types';

type ValidationResult =
  | {
      valid: true;
    }
  | {
      valid: false;
      errorMessage: string;
      errorCode: ErrorCode;
    };

const success: ValidationResult = {valid: true};
const fail = (errorMessage: string): ValidationResult => ({
  valid: false,
  errorMessage,
  errorCode: parseErrorMessageToErrorCode(errorMessage),
});

export default function validateDynamicContext({
  target,
  source,
  runtimeEnvironment,
}: {
  target: DynamicContext;
  source: DynamicContext;
  runtimeEnvironment: GatewayRuntimeEnvironment;
}): ValidationResult {
  const sourceElement = Expressions.getElementById(
    Component.getRender(source.component),
    source.elementId
  );

  if (
    // If we can't find source element in the source current component,
    // then the source element is a new element, not a moved or pasted
    // existing element or a clone of an existing element.
    !sourceElement ||
    // Only run the rest of the code if we’re moving something dynamic
    !hasDynamicContent(sourceElement)
  ) {
    return success;
  }

  const sourceBindingContextNode = getElementBindingContextNode(
    source.elementId,
    source.component,
    source.componentMap,
    source.page,
    source.collections,
    runtimeEnvironment
  );

  const targetElementBindingContextNode = getElementBindingContextNode(
    target.elementId,
    target.component,
    target.componentMap,
    target.page,
    target.collections,
    runtimeEnvironment
  );
  const targetBindingContextNode = targetElementBindingContextNode
    ? targetElementBindingContextNode.getImmediateDescendantNode()
    : null;

  // dynamic content cannot be moved from or to non-dynamic contexts
  if (!sourceBindingContextNode || !targetBindingContextNode) {
    return fail(getGenericError());
  }

  return validateElementMoveInBindingContextGraph({
    element: sourceElement,
    targetNode: targetBindingContextNode,
    sourceNode: sourceBindingContextNode,
    target,
  });
}

function validateElementMoveInBindingContextGraph({
  element,
  targetNode,
  sourceNode,
  target,
}: {
  element: ExpressionElement;
  targetNode: InternalBindingContextGraphNode;
  sourceNode: InternalBindingContextGraphNode;
  target: DynamicContext;
}): ValidationResult {
  const errorMessage = getValidationErrorMessage({
    ...analyzeSourceElement(element),
    targetNode,
    sourceNode,
    target,
  });

  return errorMessage === undefined ? success : fail(errorMessage);
}

function hasDynamicContent(element: ExpressionElement) {
  const {
    topLevelDynamicContentFlags: {
      orphanBindingsOrConditions,
      orphanContextualConditions,
      orphanContextualFilterProps,
    },
    topLevelBoundaryCreators,
  } = analyzeSourceElement(element);

  return (
    orphanBindingsOrConditions ||
    orphanContextualConditions ||
    orphanContextualFilterProps ||
    topLevelBoundaryCreators.length > 0
  );
}
